<script setup lang="ts">
    import AppSidebar from '@/views/AppSidebar.vue';
    import AppLink from '@/components/AppLink.vue';
    import AppMainView from '@/components/AppMainView.vue';
    import AppFormTitle from '@/components/forms/AppFormTitle.vue';
    import { ROUTES } from '@/router';
    import { useCaseAdminStore } from '@/views/admin/adminStore';
    import AdminStudyPanel from '@/views/admin/AdminStudyPanel.vue';
    import AdminTemplatePanel from '@/views/admin/AdminTemplatePanel.vue';
    import { computed, onMounted, onUnmounted, ref } from 'vue';
    import type { Template } from '@/domain/template/template';
    import type { ApiStudy } from '@/api/study/study';
    import { DateTime } from 'luxon';

    const store = useCaseAdminStore();

    const showAllStudies = ref(false);
    const visibleStudies = computed<ApiStudy[]>(() =>
        store.studies.filter((study) => (showAllStudies.value ? true : study === store.study)),
    );

    const showAllTemplates = ref(false);
    const visibleTemplates = computed<Template[]>(() =>
        store.templates.filter((template) =>
            showAllTemplates.value ? true : store.templateType(template) !== null,
        ),
    );

    const caseCreated = computed(() => store.case ? DateTime.fromISO(store.case.created).toHTTP() : '');
    const caseUpdated = computed(() => store.case ? DateTime.fromISO(store.case.updated).toHTTP() : '');

    onMounted(() => (store.shouldPoll = true));
    onUnmounted(() => (store.shouldPoll = false));
</script>

<template>
    <v-layout>
        <app-sidebar>
            <router-link :to="{ name: ROUTES.HOME }">
                <app-link icon="mdi-arrow-left">Back to your Cases</app-link>
            </router-link>
        </app-sidebar>

        <app-main-view>
            <app-form-title class="d-flex ga-4 align-center">
                Admin
                <v-progress-circular v-if="!store.case" indeterminate />
            </app-form-title>

            <div v-if="store.case" class="mr-4">
                <v-expansion-panels>
                    <v-expansion-panel>
                        <v-expansion-panel-title>
                            <span class="text-heading-h4">Case {{ store.caseId }}</span>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <div v-if="store.case" class="property-grid">
                                <label>link: </label>
                                <a :href="store.case.self" target="_blank">{{ store.case.self }}</a>

                                <label>side: </label>
                                <span>{{ store.case.side }}</span>

                                <label>status: </label>
                                <span>{{ store.case.status }}</span>

                                <label>created: </label>
                                {{ caseCreated }}

                                <label>created: </label>
                                {{ caseUpdated }}

                                <label>specification: </label>
                                <a :href="store.case.specification" target="_blank">
                                    {{ store.case.specification }}
                                </a>

                                <label>study: </label>
                                <a :href="store.case.study" target="_blank">
                                    {{ store.case.study }}
                                </a>

                                <label>automated-template: </label>
                                <a :href="store.case.automatedTemplate" target="_blank">
                                    {{ store.case.automatedTemplate }}
                                </a>

                                <label>manual-template: </label>
                                <a :href="store.case.manualTemplate" target="_blank">
                                    {{ store.case.manualTemplate }}
                                </a>

                            </div>
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                </v-expansion-panels>
            </div>

            <div class="d-flex align-center mt-4">
                <h3 class="text-h5 ma-4">Studies</h3>
                <v-checkbox v-model="showAllStudies" hide-details label="Show all" />
            </div>
            <div v-if="store.case" class="mr-4">
                <v-expansion-panels variant="accordion" multiple>
                    <AdminStudyPanel
                        v-for="study in visibleStudies"
                        :key="study.self"
                        :study="study"
                        :case="store.case"
                    />
                </v-expansion-panels>
            </div>

            <div class="d-flex align-center mt-4">
                <h3 class="text-h5 ma-4">Templates</h3>
                <v-checkbox v-model="showAllTemplates" hide-details label="Show all" />
            </div>
            <div v-if="store.case" class="mr-4">
                <v-expansion-panels variant="accordion" multiple>
                    <AdminTemplatePanel
                        v-for="template in visibleTemplates"
                        :key="template.self"
                        :template="template"
                        :case="store.case"
                    />
                </v-expansion-panels>
            </div>
        </app-main-view>
    </v-layout>
</template>

<style scoped lang="scss">
    .property-grid {
        display: grid;
        grid-template-columns: max-content auto;
        column-gap: 1rem;
    }

    .property-grid > label {
        font-weight: bold;
    }

    a {
        color: rgb(var(--v-theme-primary));
    }
</style>
